import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SkillsAssessmentTool = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [assessmentData, setAssessmentData] = useState({});
  const [showResults, setShowResults] = useState(false);

  const assessmentSteps = [
    {
      id: 'technical',
      title: 'Compétences Techniques',
      icon: 'Settings',
      questions: [
        { id: 'ai_knowledge', question: 'Quel est votre niveau de maîtrise en Intelligence Artificielle ?', type: 'scale' },
        { id: 'digital_tools', question: 'Utilisez-vous des outils numériques avancés au quotidien ?', type: 'boolean' },
        { id: 'data_analysis', question: 'Êtes-vous à l\'aise avec l\'analyse de données ?', type: 'scale' }
      ]
    },
    {
      id: 'leadership',
      title: 'Leadership Numérique',
      icon: 'Users',
      questions: [
        { id: 'team_management', question: 'Dirigez-vous des équipes dans un contexte digital ?', type: 'boolean' },
        { id: 'change_management', question: 'Comment gérez-vous les changements technologiques ?', type: 'scale' },
        { id: 'innovation', question: 'Promouvez-vous l\'innovation dans votre organisation ?', type: 'scale' }
      ]
    },
    {
      id: 'adaptability',
      title: 'Adaptabilité',
      icon: 'RefreshCw',
      questions: [
        { id: 'learning_speed', question: 'À quelle vitesse vous adaptez-vous aux nouvelles technologies ?', type: 'scale' },
        { id: 'continuous_learning', question: 'Consacrez-vous du temps à l\'apprentissage continu ?', type: 'boolean' },
        { id: 'problem_solving', question: 'Comment évaluez-vous vos capacités de résolution de problèmes ?', type: 'scale' }
      ]
    }
  ];

  const handleAnswer = (questionId, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const calculateScore = () => {
    const scores = Object.values(assessmentData);
    const average = scores?.reduce((sum, score) => sum + score, 0) / scores?.length;
    return Math.round(average * 20); // Convert to percentage
  };

  const getRecommendations = (score) => {
    if (score >= 80) return {
      level: 'Expert',
      color: 'green',
      message: 'Excellente maîtrise ! Vous êtes prêt pour les défis futurs.',
      actions: ['Mentoring d\'autres professionnels', 'Leadership de projets innovants', 'Veille technologique avancée']
    };
    if (score >= 60) return {
      level: 'Avancé',
      color: 'blue',
      message: 'Bon niveau avec des axes d\'amélioration identifiés.',
      actions: ['Formation spécialisée en IA', 'Certification leadership digital', 'Projets transversaux']
    };
    if (score >= 40) return {
      level: 'Intermédiaire',
      color: 'yellow',
      message: 'Bases solides, développement nécessaire.',
      actions: ['Formation fondamentale IA', 'Ateliers pratiques', 'Accompagnement personnalisé']
    };
    return {
      level: 'Débutant',
      color: 'red',
      message: 'Opportunité de croissance importante.',
      actions: ['Formation intensive', 'Coaching individuel', 'Plan de développement structuré']
    };
  };

  const ScaleInput = ({ questionId, question, value, onChange }) => (
    <div className="space-y-4">
      <p className="text-lg font-medium text-gray-900">{question}</p>
      <div className="flex items-center space-x-4">
        <span className="text-sm text-gray-500">Faible</span>
        <div className="flex space-x-2">
          {[1, 2, 3, 4, 5]?.map((rating) => (
            <button
              key={rating}
              onClick={() => onChange(questionId, rating)}
              className={`w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                value === rating
                  ? 'bg-blue-600 border-blue-600 text-white' :'border-gray-300 hover:border-blue-400'
              }`}
            >
              {rating}
            </button>
          ))}
        </div>
        <span className="text-sm text-gray-500">Élevé</span>
      </div>
    </div>
  );

  const BooleanInput = ({ questionId, question, value, onChange }) => (
    <div className="space-y-4">
      <p className="text-lg font-medium text-gray-900">{question}</p>
      <div className="flex space-x-4">
        <button
          onClick={() => onChange(questionId, 5)}
          className={`px-6 py-3 rounded-lg border-2 transition-all duration-200 ${
            value === 5
              ? 'bg-green-600 border-green-600 text-white' :'border-gray-300 hover:border-green-400'
          }`}
        >
          Oui
        </button>
        <button
          onClick={() => onChange(questionId, 1)}
          className={`px-6 py-3 rounded-lg border-2 transition-all duration-200 ${
            value === 1
              ? 'bg-red-600 border-red-600 text-white' :'border-gray-300 hover:border-red-400'
          }`}
        >
          Non
        </button>
      </div>
    </div>
  );

  if (showResults) {
    const score = calculateScore();
    const recommendations = getRecommendations(score);

    return (
      <section id="skills-assessment" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Résultats de Votre Évaluation</h2>
            <p className="text-xl text-gray-600">Découvrez votre profil de compétences et vos opportunités de développement</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
            {/* Score Display */}
            <div className="text-center space-y-4">
              <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full bg-${recommendations?.color}-100 border-8 border-${recommendations?.color}-200`}>
                <span className={`text-4xl font-bold text-${recommendations?.color}-600`}>{score}%</span>
              </div>
              <h3 className={`text-2xl font-bold text-${recommendations?.color}-600`}>Niveau {recommendations?.level}</h3>
              <p className="text-gray-700 text-lg">{recommendations?.message}</p>
            </div>

            {/* Skill Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {assessmentSteps?.map((step, index) => {
                const stepQuestions = step?.questions?.map(q => q?.id);
                const stepScores = stepQuestions?.map(id => assessmentData?.[id] || 0);
                const stepAverage = stepScores?.reduce((sum, score) => sum + score, 0) / stepScores?.length;
                const stepPercentage = Math.round(stepAverage * 20);

                return (
                  <div key={step?.id} className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <Icon name={step?.icon} size={24} className="text-blue-600" />
                      <h4 className="font-bold text-gray-900">{step?.title}</h4>
                    </div>
                    <div className="mb-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-600">Score</span>
                        <span className="text-sm font-bold text-gray-900">{stepPercentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{width: `${stepPercentage}%`}}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Recommendations */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Actions Recommandées</h4>
              <ul className="space-y-3">
                {recommendations?.actions?.map((action, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className={`text-${recommendations?.color}-600 mt-0.5`} />
                    <span className="text-gray-700">{action}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200">
                Programme Personnalisé
              </button>
              <button className="border-2 border-gray-300 text-gray-700 hover:border-gray-400 px-8 py-3 rounded-lg font-semibold transition-colors duration-200">
                Télécharger le Rapport
              </button>
              <button 
                onClick={() => setShowResults(false)}
                className="text-blue-600 hover:text-blue-700 px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                Refaire l'Évaluation
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="skills-assessment" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Évaluation Interactive des Compétences</h2>
          <p className="text-xl text-gray-600">Découvrez votre niveau actuel et identifiez vos axes de développement</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Progress Bar */}
          <div className="bg-gray-50 px-8 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Étape {currentStep + 1} sur {assessmentSteps?.length}
              </span>
              <span className="text-sm font-medium text-gray-700">
                {Math.round(((currentStep + 1) / assessmentSteps?.length) * 100)}% Complété
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{width: `${((currentStep + 1) / assessmentSteps?.length) * 100}%`}}
              ></div>
            </div>
          </div>

          {/* Current Step */}
          <div className="p-8">
            <div className="flex items-center space-x-4 mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                <Icon name={assessmentSteps?.[currentStep]?.icon} size={32} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{assessmentSteps?.[currentStep]?.title}</h3>
                <p className="text-gray-600">Évaluez vos compétences dans ce domaine</p>
              </div>
            </div>

            <div className="space-y-8">
              {assessmentSteps?.[currentStep]?.questions?.map((question) => (
                <div key={question?.id}>
                  {question?.type === 'scale' ? (
                    <ScaleInput
                      questionId={question?.id}
                      question={question?.question}
                      value={assessmentData?.[question?.id]}
                      onChange={handleAnswer}
                    />
                  ) : (
                    <BooleanInput
                      questionId={question?.id}
                      question={question?.question}
                      value={assessmentData?.[question?.id]}
                      onChange={handleAnswer}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-12">
              <button
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
                className="flex items-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                <Icon name="ChevronLeft" size={20} />
                <span>Précédent</span>
              </button>

              {currentStep === assessmentSteps?.length - 1 ? (
                <button
                  onClick={() => setShowResults(true)}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
                >
                  <span>Voir les Résultats</span>
                  <Icon name="BarChart" size={20} />
                </button>
              ) : (
                <button
                  onClick={() => setCurrentStep(Math.min(assessmentSteps?.length - 1, currentStep + 1))}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                >
                  <span>Suivant</span>
                  <Icon name="ChevronRight" size={20} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsAssessmentTool;